import React, { useState, useEffect, useCallback } from "react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
} from "recharts";
import { useAuth } from "../contexts/AuthContext";
import { fetchAnalytics, fetchUsers } from "../services/api";
import "./AnalyticsTab.css";

// ── Constants ────────────────────────────────────────────────────────────────

const TIME_RANGES = [
  { value: "1h", label: "1 Hour" },
  { value: "24h", label: "24 Hours" },
  { value: "7d", label: "7 Days" },
];

const LINE_COLORS = {
  "2xx": "#10b981",
  "4xx": "#f59e0b",
  "5xx": "#ef4444",
};

const PIE_COLORS = [
  "#6366f1",
  "#14b8a6",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#6b7280",
];

// ── Formatting helpers ───────────────────────────────────────────────────────

function formatNumber(num) {
  if (num == null) return "0";
  if (num >= 1_000_000) return (num / 1_000_000).toFixed(1) + "M";
  if (num >= 1_000) return (num / 1_000).toFixed(1) + "K";
  return num.toLocaleString();
}

function formatLatency(ms) {
  if (ms == null || ms === 0) return "0 ms";
  if (ms >= 1000) return (ms / 1000).toFixed(2) + " s";
  return Math.round(ms) + " ms";
}

// ── Sub-components ───────────────────────────────────────────────────────────

/** A single metric card for the "At a Glance" row. */
function StatCard({ label, value, icon, color, subtitle }) {
  return (
    <div className="stat-card">
      <div className="stat-card-header">
        <div className={`stat-card-icon stat-card-icon--${color}`}>{icon}</div>
      </div>
      <div className="stat-card-value">{value}</div>
      <div className="stat-card-label">{label}</div>
      {subtitle && <div className="stat-card-subtitle">{subtitle}</div>}
    </div>
  );
}

/** Placeholder shown while data is loading. */
function LoadingSkeleton() {
  return (
    <div className="analytics">
      <div className="analytics-header">
        <div className="analytics-header-left">
          <h1 className="analytics-title">API Analytics</h1>
          <p className="analytics-subtitle">Loading metrics...</p>
        </div>
      </div>
      <div className="analytics-loading">
        <div className="analytics-spinner" />
        <p>Fetching data from Cloud Monitoring...</p>
      </div>
    </div>
  );
}

/** Shown when the data fetch fails. */
function ErrorState({ message, onRetry }) {
  return (
    <div className="analytics">
      <div className="analytics-header">
        <div className="analytics-header-left">
          <h1 className="analytics-title">API Analytics</h1>
        </div>
      </div>
      <div className="analytics-error">
        <svg
          width="48"
          height="48"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          viewBox="0 0 24 24"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
        <p className="analytics-error-title">Failed to load analytics</p>
        <p className="analytics-error-message">{message}</p>
        <button className="analytics-retry-btn" onClick={onRetry}>
          Try Again
        </button>
      </div>
    </div>
  );
}

/** Custom tooltip for the time-series line chart. */
function ChartTooltip({ active, payload, label, timeRange }) {
  if (!active || !payload?.length) return null;

  const formatTime = (isoStr) => {
    const d = new Date(isoStr);
    if (timeRange === "7d") {
      return d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    }
    return d.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip-time">{formatTime(label)}</p>
      {payload.map((entry) => (
        <p
          key={entry.name}
          className="chart-tooltip-item"
          style={{ color: entry.color }}
        >
          <span
            className="chart-tooltip-dot"
            style={{ backgroundColor: entry.color }}
          />
          {entry.name}: {formatNumber(entry.value)}
        </p>
      ))}
    </div>
  );
}

// ── SVG icon helpers ─────────────────────────────────────────────────────────

const ICONS = {
  activity: (
    <svg
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  ),
  alertTriangle: (
    <svg
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  clock: (
    <svg
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  users: (
    <svg
      width="24"
      height="24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  refresh: (
    <svg
      width="18"
      height="18"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      viewBox="0 0 24 24"
    >
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </svg>
  ),
};

// ── Main component ───────────────────────────────────────────────────────────

/**
 * Analytics dashboard tab with three-tier layout:
 *   Tier A – At-a-glance stat cards
 *   Tier B – Time-series traffic chart
 *   Tier C – Pie chart (methods) + Bar chart (top paths)
 *
 * Data is fetched from the admin-gateway-analytics Cloud Function which
 * proxies the Google Cloud Monitoring API.
 */
function AnalyticsTab() {
  const { token } = useAuth();
  const [timeRange, setTimeRange] = useState("24h");
  const [data, setData] = useState(null);
  const [userCount, setUserCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Data fetching ─────────────────────────────────────────────────────────

  const loadData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    try {
      const [analyticsData, usersData] = await Promise.all([
        fetchAnalytics(token, timeRange),
        fetchUsers(token, 1, 1), // only need pagination.total_count
      ]);
      setData(analyticsData);
      setUserCount(usersData?.pagination?.total_count ?? 0);
    } catch (err) {
      setError(err.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  }, [token, timeRange]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // ── X-axis timestamp formatter ────────────────────────────────────────────

  const formatXAxis = useCallback(
    (isoStr) => {
      const d = new Date(isoStr);
      if (timeRange === "7d") {
        return d.toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        });
      }
      return d.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
      });
    },
    [timeRange]
  );

  // ── Early-return states ───────────────────────────────────────────────────

  if (loading) return <LoadingSkeleton />;
  if (error) return <ErrorState message={error} onRetry={loadData} />;
  if (!data) return <ErrorState message="No data received" onRetry={loadData} />;

  // ── Destructure response ──────────────────────────────────────────────────

  const {
    summary = {},
    time_series = [],
    by_method = [],
    top_paths = [],
  } = data;

  const errorRateColor =
    summary.error_rate_pct > 5
      ? "red"
      : summary.error_rate_pct > 1
      ? "orange"
      : "green";

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="analytics">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="analytics-header">
        <div className="analytics-header-left">
          <h1 className="analytics-title">API Analytics</h1>
          <p className="analytics-subtitle">
            Real-time metrics from Cloud API Gateway
          </p>
        </div>
        <div className="analytics-controls">
          <div className="time-range-selector">
            {TIME_RANGES.map((tr) => (
              <button
                key={tr.value}
                className={`time-range-btn ${
                  timeRange === tr.value ? "time-range-btn--active" : ""
                }`}
                onClick={() => setTimeRange(tr.value)}
              >
                {tr.label}
              </button>
            ))}
          </div>
          <button
            className="analytics-refresh-btn"
            onClick={loadData}
            title="Refresh"
          >
            {ICONS.refresh}
          </button>
        </div>
      </div>

      {/* ── Tier A: At-a-Glance Stat Cards ────────────────────────────────── */}
      <div className="stat-cards-row">
        <StatCard
          label="Total Requests"
          value={formatNumber(summary.total_requests)}
          color="indigo"
          icon={ICONS.activity}
          subtitle={`${formatNumber(summary.requests_2xx)} successful`}
        />
        <StatCard
          label="Error Rate"
          value={`${summary.error_rate_pct ?? 0}%`}
          color={errorRateColor}
          icon={ICONS.alertTriangle}
          subtitle={`${formatNumber(summary.requests_4xx)} client · ${formatNumber(
            summary.requests_5xx
          )} server`}
        />
        <StatCard
          label="Avg Latency (P50)"
          value={formatLatency(summary.avg_latency_ms)}
          color="blue"
          icon={ICONS.clock}
          subtitle={`P99: ${formatLatency(summary.p99_latency_ms)}`}
        />
        <StatCard
          label="Active API Users"
          value={formatNumber(userCount)}
          color="teal"
          icon={ICONS.users}
          subtitle="Registered consumers"
        />
      </div>

      {/* ── Tier B: Time-Series Traffic Chart ─────────────────────────────── */}
      <div className="chart-card chart-card--full">
        <div className="chart-card-header">
          <h2 className="chart-card-title">Request Traffic</h2>
          <p className="chart-card-desc">
            Requests over time by response code class
          </p>
        </div>
        {time_series.length > 0 ? (
          <ResponsiveContainer width="100%" height={320}>
            <LineChart
              data={time_series}
              margin={{ top: 5, right: 20, bottom: 5, left: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="timestamp"
                tickFormatter={formatXAxis}
                tick={{ fontSize: 12, fill: "#6b7280" }}
                axisLine={{ stroke: "#e5e7eb" }}
                tickLine={false}
              />
              <YAxis
                tickFormatter={formatNumber}
                tick={{ fontSize: 12, fill: "#6b7280" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip
                content={<ChartTooltip timeRange={timeRange} />}
              />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 13 }} />
              <Line
                type="monotone"
                dataKey="2xx"
                name="2xx"
                stroke={LINE_COLORS["2xx"]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="4xx"
                name="4xx"
                stroke={LINE_COLORS["4xx"]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="5xx"
                name="5xx"
                stroke={LINE_COLORS["5xx"]}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="chart-empty">
            <p>No traffic data available for this time range.</p>
          </div>
        )}
      </div>

      {/* ── Tier C: Method Breakdown + Top Paths ──────────────────────────── */}
      <div className="chart-row">
        {/* Pie chart — HTTP method distribution */}
        <div className="chart-card chart-card--half">
          <div className="chart-card-header">
            <h2 className="chart-card-title">Request Methods</h2>
            <p className="chart-card-desc">
              Distribution by HTTP method
            </p>
          </div>
          {by_method.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={by_method}
                  dataKey="count"
                  nameKey="method"
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  label={({ method, percent }) =>
                    `${method} (${(percent * 100).toFixed(0)}%)`
                  }
                  labelLine={true}
                >
                  {by_method.map((_, i) => (
                    <Cell
                      key={`cell-${i}`}
                      fill={PIE_COLORS[i % PIE_COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value) => formatNumber(value)}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="chart-empty">
              <p>No method data available.</p>
            </div>
          )}
        </div>

        {/* Bar chart — Top 5 API paths / endpoints */}
        <div className="chart-card chart-card--half">
          <div className="chart-card-header">
            <h2 className="chart-card-title">Top API Endpoints</h2>
            <p className="chart-card-desc">Most accessed paths</p>
          </div>
          {top_paths.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart
                data={top_paths}
                layout="vertical"
                margin={{ top: 5, right: 20, bottom: 5, left: 80 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#f0f0f0"
                  horizontal={false}
                />
                <XAxis
                  type="number"
                  tickFormatter={formatNumber}
                  tick={{ fontSize: 12, fill: "#6b7280" }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="path"
                  tick={{ fontSize: 12, fill: "#374151" }}
                  axisLine={false}
                  tickLine={false}
                  width={75}
                />
                <Tooltip
                  formatter={(value) => formatNumber(value)}
                />
                <Bar
                  dataKey="count"
                  fill="#6366f1"
                  radius={[0, 4, 4, 0]}
                  barSize={24}
                />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="chart-empty">
              <p>No endpoint data available.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AnalyticsTab;
